import { FormControl, FormHelperText, FormLabel, TextField } from '@material-ui/core';
import { Autocomplete, AutocompleteRenderOptionState } from '@material-ui/lab';
import { useField } from 'formik';
import React from 'react';
import useCommonStyles from '../../useCommonStyles';

export interface OutlinedMultiSelectFieldProps<Option> {
    name: string;
    label?: string;
    placeholder?: string;
    helperText?: string;
    options: Option[];
    getOptionLabel: (option: Option) => string;
    getOptionId?: (option: Option) => string;
    onSelectionChange?: (selectedIds: string[]) => void;
    noOptionsText?: string;
    color?: 'primary' | 'secondary';
    renderOption?: (option: Option, state: AutocompleteRenderOptionState) => React.ReactNode;
}

const OutlinedMultiSelectField = <Option,>({
    name,
    label,
    placeholder,
    helperText,
    options,
    getOptionLabel,
    getOptionId = option => (option as unknown as { id: string }).id,
    onSelectionChange,
    noOptionsText,
    color,
    renderOption,
}: OutlinedMultiSelectFieldProps<Option>) => {
    const [{ value }, meta, { setValue }] = useField<string[]>(name);
    const commonStyles = useCommonStyles();

    const hasError = !!meta.error && meta.touched;

    const selectedOptions = (value ?? [])
        .map(id => options.find(option => getOptionId(option) === id))
        .filter((option): option is Option => !!option);

    return (
        <FormControl variant="outlined" fullWidth>
            {label && <FormLabel className={commonStyles.formLabel}>{label}</FormLabel>}
            <Autocomplete<Option, true, undefined, false>
                getOptionLabel={getOptionLabel}
                noOptionsText={noOptionsText}
                onChange={(_event, selected) => {
                    const selectedIds = selected.map(getOptionId);

                    if (onSelectionChange) {
                        onSelectionChange(selectedIds);
                    } else {
                        setValue(selectedIds);
                    }
                }}
                options={options}
                renderInput={params => (
                    <TextField
                        {...params}
                        color={color}
                        error={hasError}
                        inputProps={{
                            ...params.inputProps,
                        }}
                        placeholder={placeholder}
                        variant="outlined"
                    />
                )}
                renderOption={renderOption}
                value={selectedOptions}
                multiple
            />
            {helperText && <FormHelperText style={{ marginLeft: '0px' }}>{helperText}</FormHelperText>}
            <FormHelperText error={hasError}>{hasError ? meta.error : ''}</FormHelperText>
        </FormControl>
    );
};

export default OutlinedMultiSelectField;
